import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Grid, 
  Paper,
  Button,
  Card,
  CardMedia,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import BalanceIcon from '@mui/icons-material/Balance';
import SpaIcon from '@mui/icons-material/Spa';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';

// 导入图片
import collectionsBg from '../assets/images/collections/Bg.png';

// 水晶知识数据
const crystalTypes = [
  {
    id: 1,
    name: '石英类水晶',
    description: '石英是地球上最常见的矿物之一，包括紫水晶、粉晶、黄水晶、烟水晶等。这类水晶结构稳定，能量持久，是最受欢迎的能量石。',
    examples: ['紫水晶', '粉晶', '黄水晶', '白水晶', '绿幽灵', '烟水晶']
  },
  {
    id: 2,
    name: '玉石类',
    description: '玉石类矿物质地温润，能量温和持久，包括翡翠、和田玉、青金石等。这类石头自古以来在东方文化中地位尊崇，象征着美德和高贵品质。',
    examples: ['翡翠', '和田玉', '青金石', '孔雀石', '碧玉', '独山玉']
  },
  {
    id: 3,
    name: '火成岩类',
    description: '火成岩类由岩浆冷却形成，包括黑曜石、虎眼石等。这类宝石能量强烈，多与保护、勇气和力量相关，适合需要增强个人力量的人使用。',
    examples: ['黑曜石', '虎眼石', '红虎眼', '鹰眼石', '金曜石', '熔岩石']
  },
  {
    id: 4,
    name: '变质岩类',
    description: '变质岩类是在高温高压下由其他岩石转变而成，如大理石、绿泥石等。这类水晶多与转变和蜕变有关，在人生转折点佩戴尤为适合。',
    examples: ['大理石', '绿泥石', '石墨', '蛇纹石', '云母', '绿帘石']
  }
];

const crystalAttributes = [
  {
    id: 1,
    title: '五行属性',
    description: '在东方命理学中，水晶按五行（金、木、水、火、土）分类，不同属性的水晶可以补充或平衡您命盘中缺失或过弱的五行能量。',
    icon: <BalanceIcon fontSize="large" color="primary" />
  },
  {
    id: 2,
    title: '能量频率',
    description: '每种水晶都有其独特的振动频率，科学研究表明不同晶体确实会产生微弱但可测量的能量场。选择频率与您能量匹配的水晶效果最佳。',
    icon: <StarIcon fontSize="large" color="primary" />
  },
  {
    id: 3,
    title: '治愈属性',
    description: '不同水晶被认为对特定身体系统有益，如粉晶与心脏系统相关，紫水晶与头部和神经系统相关。这种关联源于古老的能量医学理论。',
    icon: <SpaIcon fontSize="large" color="primary" />
  },
  {
    id: 4,
    title: '情感影响',
    description: '水晶能影响情绪和情感状态，如黑曜石有助于释放负面情绪，月光石增强直觉和情感敏感度，粉晶促进自爱和人际关系和谐。',
    icon: <FavoriteIcon fontSize="large" color="primary" />
  },
  {
    id: 5,
    title: '精神意义',
    description: '许多水晶在不同文化中具有重要的精神象征意义，如紫水晶在多种传统中被视为提升精神意识和直觉能力的石头，青金石与智慧和真理联系。',
    icon: <LocalFloristIcon fontSize="large" color="primary" />
  }
];

const faqs = [
  {
    question: '如何清洁和充能水晶？',
    answer: '清洁水晶的方法包括：流水冲洗、月光/阳光浸泡、盐埋法或声音净化。不同水晶适用不同的清洁方法，如紫水晶不宜长时间暴露在强烈阳光下，可能褪色。充能可通过月光浸泡（满月时效果最佳）、水晶簇放置、或意念充能进行。建议每月至少为水晶清洁充能一次。'
  },
  {
    question: '初学者应该选择哪些水晶？',
    answer: '初学者入门水晶推荐：紫水晶（平衡、冥想）、白水晶（多用途、增强其他水晶能量）、黑曜石（保护、去除负能量）、粉晶（爱与治愈）、绿幽灵（财富、机遇）。这些水晶能量温和稳定，适用范围广，价格相对亲民，是水晶收藏的良好起点。'
  },
  {
    question: '水晶的能量是科学还是迷信？',
    answer: '水晶能量存在科学和传统信仰两方面解释。科学上，晶体确实有压电效应和微弱电磁场；传统上，各文化长期记录了水晶的能量特性。虽然现代科学尚未完全证实所有传统观点，但水晶作为自然物质，其对人体微妙影响仍在研究中。持开放但理性的态度体验水晶能量是明智的。'
  },
  {
    question: '佩戴水晶有什么注意事项？',
    answer: '佩戴水晶注意事项：1) 新水晶应先清洁充能；2) 贴身佩戴效果最佳，但注意部分水晶如孔雀石含有微量有毒成分，不宜长期直接接触皮肤；3) 根据需求选择佩戴位置，如紫水晶靠近头部效果更好；4) 避免多种能量相冲的水晶同时佩戴；5) 定期清洁维护；6) 尊重水晶，视为能量伙伴而非普通饰品。'
  },
  {
    question: '水晶可以如何搭配？',
    answer: '水晶搭配原则：1) 目的协同：选择能量效果互补的水晶，如紫水晶(灵性)+黄水晶(信心)适合学习；2) 色彩和谐：同色系水晶能量往往相容，如紫色系、蓝色系；3) 元素平衡：五行相生相克原理，如火属性水晶可与木属性搭配；4) 直觉选择：最重要的是聆听内心感受，选择让您感到舒适的组合；5) 经典组合：保护三件套(黑曜石+白水晶+紫水晶)、爱情组合(粉晶+绿幽灵)等。'
  },
  {
    question: '如何辨别真假水晶？',
    answer: '辨别真假水晶方法：1) 温度测试：真水晶触感冰凉并缓慢吸收体温；2) 条纹观察：真水晶内部往往有自然生长纹理；3) 气泡检查：人造水晶常含均匀气泡；4) 硬度测试：真水晶较硬，不易被指甲划伤；5) 价格参考：过于便宜的"水晶"需谨慎；6) 光线观察：在自然光下观察，真水晶光泽自然；7) 最可靠方法是向有信誉的专业店铺购买，并索取鉴定证书。'
  }
];

const Crystal = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ position: 'relative', mb: 6 }}>
        <img 
          src={collectionsBg} 
          alt="Crystal Knowledge Background" 
          style={{ width: '100%', display: 'block' }}
        />
        <Box 
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            width: '100%',
            padding: '0 2rem',
            color: 'white'
          }}
        >
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            水晶知识
          </Typography>
          <Typography variant="h5">
            Crystal Knowledge
          </Typography>
        </Box>
      </Box>

      {/* Introduction Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Typography variant="h4" gutterBottom align="center">
              水晶与命理能量
            </Typography>
            <Typography variant="h5" gutterBottom align="center">
              Crystals & Destiny Energy
            </Typography>
            <Divider sx={{ width: '60px', height: '4px', backgroundColor: '#9c27b0', mb: 4, mx: 'auto', borderRadius: '2px' }} />
            <Typography variant="body1" paragraph align="center" sx={{ maxWidth: '800px', mx: 'auto' }}>
              水晶不仅是地球的瑰宝，更是凝聚了悠久历史的能量载体。从古埃及到中国传统命理学，水晶一直被视为连接自然能量的媒介，能够平衡人体能量场，增强特定属性，甚至影响命运走向。
            </Typography>
            <Typography variant="body1" align="center" sx={{ maxWidth: '800px', mx: 'auto' }}>
              在我们的电商平台上，我们结合现代科学与传统命理学，为您提供全面的水晶知识，帮助您了解如何选择、使用和保养这些神奇的自然礼物，让它们的能量为您的生活带来积极影响。
            </Typography>
          </Grid>
        </Grid>
      </Container>

      {/* Crystal Types */}
      <Box sx={{ bgcolor: '#f8f5fb', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" gutterBottom>
            水晶的种类
          </Typography>
          <Typography variant="h5" align="center" gutterBottom>
            Types of Crystals
          </Typography>
          <Divider sx={{ width: '60px', height: '4px', backgroundColor: '#9c27b0', mb: 5, mx: 'auto', borderRadius: '2px' }} />
          
          <Grid container spacing={4}>
            {crystalTypes.map((type) => (
              <Grid item xs={12} sm={6} key={type.id}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 3,
                    height: '100%',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary">
                    {type.name}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {type.description}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      代表水晶：
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {type.examples.map((crystal, index) => (
                        <Box 
                          key={index}
                          sx={{ 
                            bgcolor: 'rgba(156, 39, 176, 0.1)', 
                            px: 2, 
                            py: 0.5, 
                            borderRadius: 2,
                            fontSize: '0.875rem'
                          }}
                        >
                          {crystal}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Crystal Attributes */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h4" align="center" gutterBottom>
          水晶的能量属性
        </Typography>
        <Typography variant="h5" align="center" gutterBottom>
          Crystal Energy Attributes
        </Typography>
        <Divider sx={{ width: '60px', height: '4px', backgroundColor: '#9c27b0', mb: 5, mx: 'auto', borderRadius: '2px' }} />
        
        <Grid container spacing={4}>
          {crystalAttributes.map((attribute) => (
            <Grid item xs={12} md={6} key={attribute.id}>
              <Box sx={{ display: 'flex', mb: 2 }}>
                <Box sx={{ mr: 2 }}>
                  {attribute.icon}
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {attribute.title}
                  </Typography>
                  <Typography variant="body2">
                    {attribute.description}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Button 
            variant="contained" 
            color="primary"
            component={RouterLink}
            to="/ai-fortune"
            size="large"
          >
            获取个人水晶推荐
          </Button>
        </Box>
      </Container>

      {/* FAQs */}
      <Box sx={{ bgcolor: '#f8f5fb', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" gutterBottom>
            常见问题
          </Typography>
          <Typography variant="h5" align="center" gutterBottom>
            Frequently Asked Questions
          </Typography>
          <Divider sx={{ width: '60px', height: '4px', backgroundColor: '#9c27b0', mb: 5, mx: 'auto', borderRadius: '2px' }} />
          
          {faqs.map((faq, index) => (
            <Accordion key={index} sx={{ mb: 2, boxShadow: 'none', '&:before': { display: 'none' } }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  backgroundColor: 'rgba(156, 39, 176, 0.05)',
                  borderRadius: '4px',
                  '&.Mui-expanded': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0
                  }
                }}
              >
                <Typography variant="h6">{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ backgroundColor: 'rgba(156, 39, 176, 0.02)', borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px' }}>
                <Typography variant="body1">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
          
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography variant="h6" gutterBottom>
              还有其他问题？
            </Typography>
            <Button 
              variant="outlined" 
              color="primary"
              component={RouterLink}
              to="/contact"
              sx={{ mt: 1 }}
            >
              联系我们
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box sx={{ bgcolor: '#9c27b0', color: 'white', py: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" gutterBottom>
            开始您的水晶之旅
          </Typography>
          <Typography variant="body1" paragraph sx={{ mb: 4 }}>
            无论是寻找平衡能量、增强特定属性，还是纯粹欣赏水晶的自然美，我们都能为您提供专业指导。
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              color="secondary"
              sx={{ px: 4, py: 1.5, fontSize: '1rem' }}
              component={RouterLink}
              to="/collections"
            >
              浏览水晶系列
            </Button>
            <Button 
              variant="outlined" 
              sx={{ 
                px: 4, 
                py: 1.5, 
                fontSize: '1rem', 
                borderColor: 'white', 
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }}
              component={RouterLink}
              to="/shop"
            >
              购买水晶饰品
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Crystal;